/**
 * Errands · concierge-driven "life admin" pillar.
 *
 * Unlike /table /stay /escape /enjoy /care /fit which are search catalogues,
 * this pillar is a curated grid of "just ask" cards + one free-form input.
 * Every submission opens a Zoho ticket at the concierge desk and fires a
 * WhatsApp + email acknowledgement to the member.
 *
 * Rails: Papers · RTO · Runs · Home · Family — per the founder's spec
 * (04 Jul 2026).
 */
import React, { useEffect, useState, useMemo } from "react";
import { useEmbedMember } from "@/hooks/useEmbedMember";

import MemberContextBand from "../components/MemberContextBand";
const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  bg:   "#F4EFE6", card: "#FCFAF4", ink:  "#211C15",
  ink2: "#5C5447", ink3: "#8A8071",
  line: "#E5DCCB", line2:"#D8CDB7",
  brass:"#8B6914", brassSoft: "#B69152",
};

const URGENCIES = [
  { key: "routine", label: "Routine" },
  { key: "soon",    label: "This week" },
  { key: "urgent",  label: "Urgent" },
];

export default function Errands() {
  const [rails, setRails] = useState([]);
  const [cards, setCards] = useState([]);
  const [selectedRail, setSelectedRail] = useState(null); // null = show all
  const [openCard, setOpenCard] = useState(null);         // { key, title, template, rail }
  const [freeText, setFreeText] = useState("");           // free-form quick-ask
  const [toast, setToast] = useState(null);               // { ref } | { error }
  const embedMember = useEmbedMember();

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/errands/catalogue`);
        const j = await r.json();
        if (j?.ok) {
          setRails(j.rails || []);
          setCards(j.cards || []);
        }
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const visibleCards = useMemo(() => {
    if (!selectedRail) return cards;
    return cards.filter((c) => c.rail === selectedRail);
  }, [selectedRail, cards]);

  return (
    <div data-testid="errands-page"
         style={{ background: T.bg, minHeight: "100vh", color: T.ink,
                  fontFamily: "'Fraunces', Georgia, serif",
                  paddingBottom: 80 }}>
      <MemberContextBand pillar="errands" />
      {/* ── Hero ── */}
      <section style={{ maxWidth: 1160, margin: "0 auto",
                        padding: "56px 32px 32px" }}>
<p style={{ letterSpacing: "0.16em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 12px",
                    fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
          /errands · by the Concierge®
        </p>
        <h1 style={{ fontSize: "clamp(30px, 5vw, 52px)", lineHeight: 1.08,
                     fontWeight: 500, margin: "0 0 14px" }}>
          Life admin. <em style={{ color: T.brass }}>Quietly handled.</em>
        </h1>
        <p style={{ color: T.ink2, fontSize: 17, lineHeight: 1.55,
                    maxWidth: 620, margin: 0,
                    fontFamily: "'Inter', sans-serif" }}>
          Our team handles the errands you don't have time for. Passports, RTO,
          bank runs, home coordination — pick a card or just tell us.
          You'll hear from a Concierge® on WhatsApp shortly.
        </p>
      </section>

      {/* ── Rail chips ── */}
      <section style={{ maxWidth: 1160, margin: "0 auto",
                        padding: "6px 32px 20px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10,
                      fontFamily: "'Inter', sans-serif" }}>
          <button
            data-testid="errands-rail-all"
            type="button"
            onClick={() => setSelectedRail(null)}
            style={pillStyle(!selectedRail)}
          >
            All errands
          </button>
          {rails.map((r) => (
            <button
              key={r.key}
              data-testid={`errands-rail-${r.key}`}
              type="button"
              onClick={() => setSelectedRail(r.key)}
              style={pillStyle(selectedRail === r.key)}
              title={r.hint}
            >
              {r.label}
            </button>
          ))}
        </div>
      </section>

      {/* ── Card grid ── */}
      <section style={{ maxWidth: 1160, margin: "0 auto",
                        padding: "12px 32px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 16,
        }}>
          {visibleCards.map((c) => (
            <button
              key={c.key}
              data-testid={`errands-card-${c.key}`}
              type="button"
              onClick={() => setOpenCard(c)}
              style={{
                textAlign: "left", cursor: "pointer",
                background: T.card, border: `1px solid ${T.line}`,
                borderRadius: 14, padding: "20px 20px 18px",
                color: T.ink, fontFamily: "inherit",
                transition: "transform 160ms ease, box-shadow 160ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 28px rgba(33,28,21,0.09)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{ letterSpacing: "0.14em", fontSize: 10,
                            color: T.brass, textTransform: "uppercase",
                            marginBottom: 8, fontWeight: 700,
                            fontFamily: "'Inter', sans-serif" }}>
                {(rails.find((r) => r.key === c.rail) || {}).label || c.rail}
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 500, margin: "0 0 6px",
                           lineHeight: 1.25 }}>
                {c.title}
              </h3>
              <p style={{ fontFamily: "'Inter', sans-serif",
                          fontSize: 13, color: T.ink2, lineHeight: 1.5,
                          margin: 0 }}>
                {c.blurb}
              </p>
            </button>
          ))}
        </div>
      </section>

      {/* ── Free-form quick ask ── */}
      <section style={{ maxWidth: 1160, margin: "36px auto 0",
                        padding: "24px 32px",
                        borderTop: `1px solid ${T.line}` }}>
        <p style={{ letterSpacing: "0.16em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 10px",
                    fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
          Anything else — just tell us.
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap",
                      alignItems: "stretch" }}>
          <input
            data-testid="errands-freeform-input"
            type="text"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="e.g., Book my car service · Change my WiFi provider · Coordinate a house painter"
            style={{
              flex: 1, minWidth: 260,
              padding: "14px 18px", fontSize: 15, borderRadius: 999,
              border: `1px solid ${T.line2}`, background: T.card,
              color: T.ink, fontFamily: "'Inter', sans-serif",
              outline: "none",
            }}
          />
          <button
            data-testid="errands-freeform-submit"
            type="button"
            disabled={freeText.trim().length < 3}
            onClick={() => setOpenCard({
              key: "custom", rail: "runs", title: "Custom errand",
              template: freeText.trim(),
            })}
            style={{
              padding: "14px 26px", fontSize: 14, fontWeight: 600,
              background: T.ink, color: T.bg,
              border: 0, borderRadius: 999,
              cursor: freeText.trim().length < 3 ? "not-allowed" : "pointer",
              opacity: freeText.trim().length < 3 ? 0.45 : 1,
              letterSpacing: "0.06em", textTransform: "uppercase",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Send to Concierge® →
          </button>
        </div>
      </section>

      {openCard && (
        <RequestModal
          card={openCard}
          embedMember={embedMember}
          onClose={() => setOpenCard(null)}
          onSuccess={(ref) => {
            setOpenCard(null);
            setFreeText("");
            setToast({ ref });
            setTimeout(() => setToast(null), 6000);
          }}
          onError={(msg) => setToast({ error: msg })}
        />
      )}

      {toast && (
        <div
          data-testid={toast.error ? "errands-toast-error" : "errands-toast-success"}
          role="status"
          style={{
            position: "fixed", bottom: 28, left: "50%",
            transform: "translateX(-50%)",
            background: T.ink, color: T.bg,
            borderRadius: 12, padding: "16px 24px",
            fontSize: 14, boxShadow: "0 12px 32px rgba(33,28,21,0.28)",
            zIndex: 60, maxWidth: "92vw", fontFamily: "'Inter', sans-serif",
          }}
        >
          {toast.error ? (
            toast.error
          ) : (
            <>
              <div style={{ color: T.brassSoft, letterSpacing: "0.14em",
                            fontSize: 10, textTransform: "uppercase",
                            fontWeight: 700, marginBottom: 4 }}>
                Concierge® has it
              </div>
              We'll reach out on WhatsApp shortly. Reference:{" "}
              <span style={{ fontFamily: "ui-monospace, Menlo, monospace",
                             fontSize: 12 }}>{toast.ref}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function pillStyle(active) {
  return {
    background: active ? "#211C15" : "#FCFAF4",
    color: active ? "#F4EFE6" : "#211C15",
    border: `1px solid ${active ? "#211C15" : "#E5DCCB"}`,
    borderRadius: 999,
    padding: "9px 18px",
    fontSize: 13, fontWeight: 500,
    cursor: "pointer",
    letterSpacing: active ? "0.06em" : 0,
  };
}

// ── Modal ──────────────────────────────────────────────────────
function RequestModal({ card, embedMember, onClose, onSuccess, onError }) {
  const [brief, setBrief]     = useState(card.template || "");
  const [when,  setWhen]      = useState("");
  const [where, setWhere]     = useState("");
  const [urgency, setUrgency] = useState("routine");
  const [name,  setName]      = useState(embedMember?.name  || "");
  const [email, setEmail]     = useState(embedMember?.email || "");
  const [phone, setPhone]     = useState(embedMember?.phone || "");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (brief.trim().length < 3) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/errands/request`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_key: card.key,
          rail:     card.rail,
          title:    card.title,
          brief,
          urgency,
          when:  when  || null,
          where: where || null,
          member_name:  name  || null,
          member_email: email || null,
          member_phone: phone || null,
        }),
      });
      const j = await r.json();
      if (r.ok && j?.ok) {
        onSuccess(j.member_reference || j.display_ref || j.ticket_id);
      } else {
        onError(j?.detail || "We couldn't send that just now. Please try again.");
      }
    } catch (_) {
      onError("Network hiccup — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div
        data-testid="errands-modal-backdrop"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(15,12,8,0.44)", zIndex: 90,
        }}
      />
      <div
        data-testid="errands-modal"
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, 92vw)", maxHeight: "90vh",
          overflowY: "auto",
          background: T.card, borderRadius: 16, padding: "28px 30px",
          boxShadow: "0 24px 64px rgba(15,12,8,0.32)",
          zIndex: 100, fontFamily: "'Inter', sans-serif",
        }}
      >
        <p style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 6px",
                    fontWeight: 700 }}>
          {card.rail || "request"}
        </p>
        <h2 style={{ fontFamily: "'Fraunces', Georgia, serif",
                     fontSize: 26, fontWeight: 500, margin: "0 0 18px",
                     lineHeight: 1.15, color: T.ink }}>
          {card.title || "Send to Concierge®"}
        </h2>

        <Field label="Brief" required>
          <textarea
            data-testid="errands-modal-brief"
            value={brief} onChange={(e) => setBrief(e.target.value)}
            placeholder="Tell us what you need — the concierge will follow up on WhatsApp."
            rows={4}
            style={{ ...textareaStyle }}
          />
        </Field>

        <div style={{ display: "grid",
                      gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="When">
            <input value={when} onChange={(e) => setWhen(e.target.value)}
                   placeholder="ASAP · This week · 15 Aug"
                   data-testid="errands-modal-when"
                   style={inputStyle} />
          </Field>
          <Field label="Where">
            <input value={where} onChange={(e) => setWhere(e.target.value)}
                   placeholder="City / area"
                   data-testid="errands-modal-where"
                   style={inputStyle} />
          </Field>
        </div>

        <Field label="Urgency">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {URGENCIES.map((u) => (
              <button
                key={u.key} type="button"
                data-testid={`errands-modal-urgency-${u.key}`}
                onClick={() => setUrgency(u.key)}
                style={{
                  padding: "8px 14px", borderRadius: 999,
                  border: `1px solid ${urgency === u.key ? T.ink : T.line2}`,
                  background: urgency === u.key ? T.ink : T.card,
                  color: urgency === u.key ? T.bg : T.ink,
                  cursor: "pointer", fontSize: 12.5, fontWeight: 500,
                }}>
                {u.label}
              </button>
            ))}
          </div>
        </Field>

        <div style={{ marginTop: 14, paddingTop: 14,
                      borderTop: `1px solid ${T.line}` }}>
          <p style={{ letterSpacing: "0.12em", fontSize: 10, color: T.ink3,
                      textTransform: "uppercase", margin: "0 0 10px",
                      fontWeight: 700 }}>
            So we can reach you
          </p>
          <div style={{ display: "grid",
                        gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Name">
              <input value={name} onChange={(e) => setName(e.target.value)}
                     data-testid="errands-modal-name" style={inputStyle} />
            </Field>
            <Field label="WhatsApp">
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder="+91…"
                     data-testid="errands-modal-phone" style={inputStyle} />
            </Field>
          </div>
          <Field label="Email">
            <input value={email} onChange={(e) => setEmail(e.target.value)}
                   data-testid="errands-modal-email" style={inputStyle} />
          </Field>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end",
                      marginTop: 22 }}>
          <button
            data-testid="errands-modal-cancel"
            type="button" onClick={onClose}
            style={{
              background: "transparent", color: T.ink2,
              border: `1px solid ${T.line2}`, borderRadius: 999,
              padding: "11px 20px", fontSize: 13, cursor: "pointer",
            }}>
            Cancel
          </button>
          <button
            data-testid="errands-modal-submit"
            type="button" onClick={submit}
            disabled={brief.trim().length < 3 || sending}
            style={{
              background: T.ink, color: T.bg, border: 0,
              borderRadius: 999, padding: "11px 22px",
              fontSize: 13, fontWeight: 600,
              letterSpacing: "0.06em", textTransform: "uppercase",
              cursor: sending ? "wait" : "pointer",
              opacity: brief.trim().length < 3 || sending ? 0.5 : 1,
            }}>
            {sending ? "Sending…" : "Send to Concierge® →"}
          </button>
        </div>
      </div>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{
        display: "block",
        letterSpacing: "0.12em", fontSize: 10.5, color: T.ink3,
        textTransform: "uppercase", marginBottom: 6, fontWeight: 700,
      }}>
        {label}{required ? " *" : ""}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 14px", fontSize: 14,
  borderRadius: 8, border: `1px solid ${T.line2}`,
  background: T.card, color: T.ink,
  fontFamily: "'Inter', sans-serif", outline: "none",
};
const textareaStyle = {
  ...inputStyle, resize: "vertical",
  fontFamily: "'Inter', sans-serif",
};
